/**
 * Shared client-side UUID guard for URL/param-driven detail queries.
 *
 * Why this exists (the recurring-bug lesson): a malformed id from `useParams()`
 * (truncated link, embedded whitespace from a copy-paste, literal "undefined")
 * passes a bare `!!id` check and reaches Postgres, which rejects it with
 * `22P02 invalid input syntax for type uuid`. The guard was first added
 * locally in `useProperty` (Sentry DALLALI-FRONTEND-5R) but never shared, so
 * the identical bug resurfaced in `useContract` (Sentry DALLALI-FRONTEND-DZ,
 * issue #3297). Every detail hook keyed by an id that can come from a URL
 * must gate `enabled:` with `isValidUUID(id)` — from THIS module, never a
 * local copy.
 *
 * Version-agnostic on purpose: `[1-8]` accepts v1–v8 (new identifiers are
 * UUIDv7, legacy rows are v4 — see apps/api/docs/conventions.md §10; pinning
 * versions rejects real rows). The nil and max UUIDs are also accepted: Nest's
 * `all`-mode `ParseUUIDPipe` accepts them, and dropping ids the server would
 * serve broke the brokerage soft-warn surface once already (#2257).
 */
export const UUID_REGEX =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;

export function isValidUUID(value: string | null | undefined): value is string {
  if (!value) return false;
  return UUID_REGEX.test(value);
}
